import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { PromptData } from '../types';
import { trackEvent } from '../src/gtag';
import { useToast } from '../hooks/useToast';
import promptsData from '../services/data/prompts.json';

interface PromptGeneratorProps {
  categoryKey: string;
}

const placeholderRegex = /{(\w+)}/g;

export const PromptGenerator: React.FC<PromptGeneratorProps> = ({ categoryKey }) => {
  // Use imported data directly
  const promptData: PromptData = promptsData as unknown as PromptData;

  const [error, setError] = useState<string | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<string>('');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { showToast } = useToast();

  useEffect(() => {
    // Initialize template
    if (promptData && promptData[categoryKey]?.templates?.length > 0) {
        setCurrentTemplate(promptData[categoryKey].templates[0]);
    }
  }, [categoryKey, promptData]);

  useEffect(() => {
    // Check for shared prompt in URL on initial load
    const params = new URLSearchParams(window.location.search);
    const sharedPromptBase64 = params.get('shared');
    if (sharedPromptBase64) {
      try {
        // Decode with URI component handling for special characters
        const decodedPrompt = decodeURIComponent(atob(sharedPromptBase64));
        setGeneratedPrompt(decodedPrompt);
        // Clean the URL to avoid re-triggering
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) {
        console.error("Failed to decode shared prompt:", e);
      }
    }
  }, []);

  const placeholders = useMemo(() => {
    if (!currentTemplate) return [];
    const matches = currentTemplate.match(placeholderRegex);
    if (!matches) return [];
    // Use a Set to get unique placeholders
    return Array.from(new Set(matches.map(p => p.slice(1, -1))));
  }, [currentTemplate]);

  useEffect(() => {
    const newInputs: Record<string, string> = {};
    placeholders.forEach(p => {
      newInputs[p] = inputs[p] || '';
    });
    setInputs(newInputs);
    setGeneratedPrompt(''); // Clear generated prompt when template changes
    setErrors({}); // Clear errors on template change
  }, [placeholders, currentTemplate]); // Rerun when template changes
  
  const handleInputChange = (placeholder: string, value: string) => {
    setInputs(prev => ({ ...prev, [placeholder]: value }));
    // Clear error for this field on input
    if (errors[placeholder]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[placeholder];
        return newErrors;
      });
    }
  };
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    placeholders.forEach(p => {
      if (!inputs[p]?.trim()) {
        newErrors[p] = 'This field is required.';
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleGenerate = () => {
    if (!validate()) {
      return;
    }
    
    let newPrompt = currentTemplate;
    for (const key in inputs) {
      const regex = new RegExp(`{${key}}`, 'g');
      newPrompt = newPrompt.replace(regex, inputs[key].trim());
    }
    setGeneratedPrompt(newPrompt);

    // GA4 Event Tracking
    if (promptData && promptData[categoryKey]) {
      trackEvent({
        action: 'prompt_generated',
        category: promptData[categoryKey].name,
        label: currentTemplate, // template_name
        value: newPrompt.length, // prompt_length
      });
    }
  };
  
  const handleRandom = useCallback(() => {
    if (!promptData || !promptData[categoryKey]) return;
    const templates = promptData[categoryKey].templates;
    const randomIndex = Math.floor(Math.random() * templates.length);
    setCurrentTemplate(templates[randomIndex]);
  }, [promptData, categoryKey]);
  
  const handleCopy = () => {
    if (!generatedPrompt) return;
    navigator.clipboard.writeText(generatedPrompt).then(() => {
      showToast('Prompt copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      showToast('Failed to copy prompt.');
    });
  };

  const handleShare = () => {
    if (!generatedPrompt) return;
    const promptToEncode = generatedPrompt.substring(0, 300);
    // Use encodeURIComponent to handle special characters, then btoa
    const encodedPrompt = btoa(encodeURIComponent(promptToEncode));
    const shareUrl = `${window.location.origin}${window.location.pathname}?shared=${encodedPrompt}&utm_source=brandgenius&utm_medium=promptshare&utm_campaign=ai_prompt_generator`;

    navigator.clipboard.writeText(shareUrl).then(() => {
      showToast('Sharable link copied!');
    }).catch(err => {
      console.error('Failed to copy share link: ', err);
      showToast('Failed to copy link.');
    });
  };

  const handleDownload = () => {
    if (!generatedPrompt) return;
    const blob = new Blob([generatedPrompt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${categoryKey}-prompt.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  if (error) {
    return <div className="text-center p-8 text-red-600 bg-red-50 rounded-lg">{error}</div>;
  }
  
  if (!promptData || !promptData[categoryKey]) {
    return <div className="text-center p-8 text-gray-500">No prompts found for this category.</div>;
  }

  const templates = promptData[categoryKey].templates;

  return (
    <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 mt-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="template-select" className="block text-sm font-medium text-[#6B7280] mb-1">Select a Template</label>
          <select
            id="template-select"
            value={currentTemplate}
            onChange={(e) => setCurrentTemplate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-[#1F2937] focus:ring-2 focus:ring-[#4F46E5] focus:border-[#4F46E5] transition-shadow"
            aria-label="Select a prompt template"
          >
            {templates.map((template, index) => (
              <option key={index} value={template}>{template.substring(0, 50)}...</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-transparent mb-1">Or</label>
           <button onClick={handleRandom} className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300">
            Use a Random Prompt
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {placeholders.map(p => (
          <div key={p}>
            <div className="flex justify-between items-center mb-1">
              <label htmlFor={`input-${p}`} className="block text-sm font-medium text-[#6B7280] capitalize">
                {p.replace(/_/g, ' ')}
              </label>
              <span className="text-xs text-gray-400">
                {inputs[p]?.length || 0} / 100
              </span>
            </div>
            <input
              type="text"
              id={`input-${p}`}
              value={inputs[p] || ''}
              onChange={(e) => handleInputChange(p, e.target.value)}
              className={`w-full px-4 py-2 border rounded-md bg-white text-[#1F2937] focus:ring-2 transition-shadow ${errors[p] ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#4F46E5] focus:border-[#4F46E5]'}`}
              placeholder={`e.g., a haunted forest`}
              aria-label={`Input for ${p.replace(/_/g, ' ')}`}
              maxLength={100}
              aria-invalid={!!errors[p]}
              aria-describedby={errors[p] ? `${p}-error` : undefined}
            />
            {errors[p] && <p id={`${p}-error`} className="mt-1 text-xs text-red-600">{errors[p]}</p>}
          </div>
        ))}
      </div>
      
      <div className="pt-6">
        <button
          onClick={handleGenerate}
          className="w-full flex justify-center items-center gap-2 py-3 px-4 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300 disabled:opacity-50"
          aria-label="Generate the final prompt"
          disabled={placeholders.some(p => !inputs[p]?.trim())}
        >
          Generate Prompt
        </button>
      </div>

      {generatedPrompt && (
        <div className="mt-8">
          <label className="block text-sm font-medium text-[#6B7280] mb-1">Your Generated Prompt</label>
          <div className="w-full p-4 border border-gray-300 rounded-md bg-gray-50 text-[#1F2937] min-h-[100px]" aria-live="polite">
            {generatedPrompt}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <button onClick={handleCopy} className="px-4 py-2 bg-[#10B981] hover:bg-[#34D399] text-white font-semibold rounded-lg shadow-sm transition-colors duration-300" aria-label="Copy generated prompt">
              Copy Prompt
            </button>
            <button onClick={handleShare} className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg shadow-sm transition-colors duration-300" aria-label="Copy shareable link for prompt">
              Share Prompt
            </button>
            <button onClick={handleDownload} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300" aria-label="Download generated prompt as a text file">
              Download (.txt)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};